import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useZero } from "@/hooks/use-zero";
import { useSession } from "@/features/auth/use-session";
import { createCollection } from "@/lib/mutators";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const PALETTE = [
  "var(--color-red-500, #ef4444)",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#a78bfa",
  "#ec4899",
];

export function NewCollectionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const z = useZero();
  const { data: session } = useSession();
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(PALETTE[0]!);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!session?.user) return;
    setBusy(true);
    try {
      const id = await createCollection(z, session.user.id, {
        name: name.trim() || "Untitled",
        themeColor: color,
      });
      setName("");
      onOpenChange(false);
      toast.success("Collection created");
      navigate(`/collections/${id}`);
    } catch (err) {
      console.error(err);
      toast.error("Could not create collection", { description: String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New collection</DialogTitle>
          <DialogDescription>
            Group photos, notes and events together. You can rename it later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              autoFocus
              placeholder="e.g. Trip to Mallorca"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Theme</Label>
            <div className="flex gap-2">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    "h-7 w-7 rounded-full transition",
                    c === color && "ring-2 ring-ring ring-offset-2 ring-offset-background",
                  )}
                  style={{ background: c }}
                  aria-label={`Pick color ${c}`}
                />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create collection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
